import dynamic from 'next/dynamic';

import { matchSorter } from 'match-sorter';

// Types
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { getRandomColor } from '@/utils/getRandomIcoColor';
import { sortGroups } from '@/utils/sort/profiles/sortGroups';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';
import useToggle from '@/utils/hooks/utils/useToggle';

// Components
import Button from '@/components/base/Button';
import { SearchBox } from '@/components/composite/SearchBox/SearchBox';
import { SearchGroupsItems } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/GroupsTab/SearchGroupsItems';
import { SearchInput } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/SearchInput';
import { SearchListWrapper } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/SearchListWrapper';

const FunnelSpGroupModal = dynamic(() =>
    import('@/components/composite/SpGroups/FunnelSpGroupModal').then(
        (mod) => mod.FunnelSpGroupModal
    )
);

export type GroupsFormattedType = SpGroupType & {
    selected: boolean;
};

export function GroupsTab() {
    const { t } = useTranslation('layout_platform');
    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });
    const { manageGroups, activeGroupId } = useProfilesStore((state) => state);
    const { isOpen: isOpenSpGroupFunnel, toggle: toggleSpGroupFunnel } = useToggle();

    const getInitialData = () => {
        const formattedGroups = activatedSpGroups.map((group) => ({
            ...group,
            disabled: true,
            social_profiles: group.social_profiles.filter((profile) => profile.active),
            selected: group.id === activeGroupId
        }));

        return matchSorter(formattedGroups, '', {
            baseSort: (a, b) => sortGroups('sort_asc', a.item, b.item)
        });
    };

    if (!activatedSpGroups.length) {
        return (
            <>
                <div className="flex h-80 flex-col items-center justify-center gap-4 px-6 text-center">
                    <div className="flex flex-col gap-1">
                        <span className="font-GSemiBold text-sm text-icoBlack dark:text-white">
                            {t('manage_profiles.tab_groups.search_no_result')}
                        </span>
                        <span className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {t('manage_profiles.tab_groups.empty_state_description')}
                        </span>
                    </div>
                    <Button size="sm" variant="secondary" onClick={toggleSpGroupFunnel}>
                        {t('manage_profiles.tab_groups.empty_state_cta')}
                    </Button>
                </div>
                <FunnelSpGroupModal
                    open={isOpenSpGroupFunnel}
                    onDismiss={toggleSpGroupFunnel}
                    initialData={{ fallBackStyle: getRandomColor() }}
                />
            </>
        );
    }

    return (
        <SearchBox
            key={activeGroupId}
            searchKeys={{
                threshold: matchSorter.rankings.CONTAINS,
                keys: ['name']
            }}
            isMultiSelect={false}
            inputId="groups"
            initialData={getInitialData()}>
            <SearchBox.Wrapper<GroupsFormattedType>
                withoutStyle
                data-testid="search-box-wrapper-groups"
                className="flex h-80 flex-col"
                onSelectionChange={(groups) => manageGroups(groups[0])}>
                <SearchInput placeholder={t('manage_profiles.tab_groups.search_placeholder')} />
                <SearchListWrapper
                    type="groups"
                    no_data_message={t(
                        'layout_platform:manage_profiles.tab_groups.search_no_result'
                    )}>
                    <SearchGroupsItems />
                </SearchListWrapper>
            </SearchBox.Wrapper>
        </SearchBox>
    );
}
